// tiny helper to turn unknown error like values into a readable toast message
// comments are lowercase per project rules

export function toUserMessage(input: unknown): string {
  const fallback = "something went wrong";

  try {
    if (input instanceof Error) {
      return extractFromString(input.message) || fallback;
    }

    // direct strings may be raw json from server
    if (typeof input === "string") {
      const t = input.trim();
      return extractFromString(t) || fallback;
    }

    if (input && typeof input === "object") {
      const o = input as Record<string, unknown>;
      // common api error shapes
      const fromDirect = pickFirstString(
        o,
        "message",
        "error",
        "detail",
        "msg",
        "reason"
      );
      if (fromDirect) return extractFromString(fromDirect) || fallback;

      if (typeof o.error === "object" && o.error) {
        const nested = toUserMessage(o.error as unknown);
        if (nested) return nested;
      }

      // fallback to a compact json summary without braces noise
      const compact = safeJson(o);
      if (compact) return compact;
    }
  } catch {
    // ignore extraction errors and fall through
  }

  return fallback;
}

// tries to parse a string for a meaningful message including json bodies
function extractFromString(s: string): string {
  if (!s) return "";

  const out = s.replace(/^Error:\s*/i, "").trim();

  if ((out.startsWith("{") && out.endsWith("}")) || (out.startsWith("[") && out.endsWith("]"))) {
    try {
      const parsed = JSON.parse(out);
      const inner = toUserMessage(parsed);
      if (inner && inner !== "something went wrong") return inner;
    } catch {
      // fall back to raw string
    }
  }

  // collapse simple brace wrapped single field objects like error msg
  const braceMatch = out.match(/^\{\s*"?(message|error|detail|msg|reason)"?\s*:\s*"([^"]*)"\s*\}$/i);
  if (braceMatch) return braceMatch[2];

  return out;
}

function pickFirstString(obj: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

// safe and compact json representation of an object for display when no field is available
function safeJson(obj: unknown): string {
  try {
    const seen = new WeakSet();
    const str = JSON.stringify(
      obj,
      (_key, value) => {
        if (typeof value === "object" && value !== null) {
          if (seen.has(value as object)) return "[circular]";
          seen.add(value as object);
        }
        return value;
      },
      0
    );
    // try to extract a message-like value if json is a simple object
    const m = str.match(/^\{\s*"?(message|error|detail|msg|reason)"?\s*:\s*"([^"]*)"\s*\}$/i);
    if (m) return m[2];
    return str;
  } catch {
    return "";
  }
}
